import type { TIconPack } from "@ch/assets/icons";
import type { FC, ReactNode } from "react";
import { NavLink, type NavLinkProps } from "react-router-dom";
import type { BaseButtonProps } from "../Buttons/BaseButton";
import type { ButtonProps } from "../Buttons/Button";
import {
  baseVariants,
  equalSizes,
  iconSizes,
  loaderSizes,
} from "../Buttons/baseVariants";
import Icon, { type IconProps } from "../Icons/Icon";
import variants from "./variants";

export type NavLinkButtonProps = Omit<NavLinkProps, "className"> & {
  children?: ReactNode;
  leftIcon?: TIconPack;
  rightIcon?: TIconPack;
  leftIconProps?: Omit<IconProps, "name">;
  rightIconProps?: Omit<IconProps, "name">;
  isLoading?: boolean;
  className?: string;
  buttonStyle?: NavLinkProps["className"];
  shape?: BaseButtonProps["shape"];
  size?: BaseButtonProps["size"];
  variant?: ButtonProps["variant"];
  iconOnly?: boolean;
};

const NavLinkButton: FC<NavLinkButtonProps> = ({
  children,
  leftIcon,
  rightIcon,
  leftIconProps,
  rightIconProps,
  isLoading,
  className = "",
  buttonStyle,
  shape = "rounded",
  size = "base",
  variant = "primary",
  iconOnly = false,
  ...props
}) => {
  const shapeClassName = baseVariants.shape[shape];
  const sizeClassName =
    shape === "icon" ? equalSizes[size] : baseVariants.size[size];
  const loaderSizeClassName = loaderSizes[size];

  const iconSize = iconSizes[size];

  return (
    <NavLink aria-busy={isLoading} className={buttonStyle} {...props}>
      {({ isActive }) => (
        <div
          className={[
            variants[variant][isActive ? "active" : "inactive"].button,
            `flex items-center gap-3 transition-all hover:opacity-95 active:scale-95 ${shapeClassName} ${sizeClassName} ${className}`,
          ].join(" ")}
        >
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <span
                aria-label="Loading"
                className={`loading loading-dots ${loaderSizeClassName}`}
              />
            </div>
          ) : (
            <>
              {leftIcon && (
                <Icon
                  color={
                    variants[variant][isActive ? "active" : "inactive"].icon
                  }
                  name={leftIcon}
                  size={iconSize}
                  {...leftIconProps}
                />
              )}
              {!iconOnly && <span>{children}</span>}
              {rightIcon && (
                <Icon
                  color={
                    variants[variant][isActive ? "active" : "inactive"].icon
                  }
                  name={rightIcon}
                  size={iconSize}
                  {...rightIconProps}
                />
              )}
            </>
          )}
        </div>
      )}
    </NavLink>
  );
};

export default NavLinkButton;
